import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import { AttentionIcon } from "components/icons/AttentionIcon";
import { useEffect, useState } from "react";
import { useQuery } from "hooks/useQuery";
import { usePrefetchFilingEndpoints } from "store/apis/filing";
import {
  EntityWithSeasonAndCount,
  TaskListBaseQueryParams,
} from "types/Models/task";
import { EntityTaskTable } from "./EntityTaskTable";
import { Corporate } from "components/icons/Corporate";
import { LoadingIcon } from "components/icons/LoadingIcon";
import { motion } from "framer-motion";

export const CustomLoader = () => (
  <motion.div
    data-testid="loader-v0"
    id="loader-v0"
    layout
    className="t-flex t-justify-center t-items-center t-h-4"
  >
    <div className="t-animate-spin t-border-solid t-border-t-surface-transparent t-rounded-full t-border-neutral-50 t-h-3 t-w-3 t-border-[1px]" />
  </motion.div>
);

export const TaskStat = ({
  title,
  count,
}: {
  title: string;
  count: number;
}) => (
  <div className="t-flex t-items-center t-gap-1.5">
    <AttentionIcon className="t-text-orange t-h-4 t-w-4" />
    <span className="t-text-body-sm t-text-text-30">
      {count} {title}
    </span>
  </div>
);

export const EntityTaskList = ({
  taskSeasonsWithEntities,
  isParentFetching,
  isAnyFilterActive,
  resetFilter,
  params,
  isFetching,
}: {
  taskSeasonsWithEntities: EntityWithSeasonAndCount[];
  isParentFetching: boolean;
  isAnyFilterActive: boolean;
  resetFilter: () => void;
  params: TaskListBaseQueryParams;
  isFetching: boolean;
}) => {
  const prefetchTasks = usePrefetchFilingEndpoints("getAllTasksByEntity");
  const query = useQuery();
  const highlightEntityId = query.get("entity");
  const [openEntityIds, setOpenEntityIds] = useState<string[]>(
    highlightEntityId ? [highlightEntityId] : []
  );

  useEffect(() => {
    taskSeasonsWithEntities.forEach((entity) => {
      const seasonsWithTasks = Object.entries(entity.season_task_counts).filter(
        ([, count]) => count > 0
      );

      seasonsWithTasks.forEach(([season]) => {
        prefetchTasks({
          ...params,
          filing_season: season,
          entity_id: entity.uuid,
        });
      });
    });
  }, [taskSeasonsWithEntities, params, prefetchTasks]);

  if (taskSeasonsWithEntities?.length === 1) {
    const firstEntity = taskSeasonsWithEntities[0];

    return (
      <EntityTaskTable
        entityId={firstEntity.uuid}
        taskSeasonsWithEntity={firstEntity}
        isParentFetching={isParentFetching}
        resetFilter={resetFilter}
        isAnyFilterActive={isAnyFilterActive}
        isSingleEntity
        params={params}
      />
    );
  }

  return (
    <Accordion.Root
      type="multiple"
      value={openEntityIds}
      onValueChange={setOpenEntityIds}
      className="t-flex t-gap-3 t-flex-col"
    >
      {taskSeasonsWithEntities
        .filter((entity) => Object.keys(entity.season_task_counts)?.length > 0)
        .map((entity) => {
          const totalTasks = Object.values(entity.season_task_counts).reduce(
            (a, b) => a + b,
            0
          );

          return (
            <Accordion.Item value={entity.uuid} key={entity.uuid}>
              <Accordion.Trigger className="after:data-state-open:t-border-none t-px-5 t-py-4 sm:t-px-4 sm:t-py-3">
                <div className="t-w-full t-flex t-justify-between t-items-center">
                  <div className="t-flex t-gap-3 t-items-center">
                    <div className="t-w-[38px] t-h-[38px] t-shrink-0 t-rounded-[22px] t-flex t-items-center t-justify-center  t-bg-surface-lighter-grey sm:t-hidden">
                      <Corporate className="t-text-neutral  t-w-[14px] t-h-[14px]" />
                    </div>

                    <div>
                      <div className="t-text-subtext t-text-text-60">
                        {entity.registered_name}
                      </div>
                      <div className="sm:t-mt-0.5 sm:t-flex sm:t-flex-wrap sm:t-items-center sm:t-gap-x-3 sm:t-gap-y-0.5">
                        {isFetching ? (
                          <div className="t-flex t-items-center">
                            <CustomLoader />

                            <span className="t-ml-2 t-text-[12px] t-text-neutral-50">
                              Loading Filings..
                            </span>
                          </div>
                        ) : (
                          <div className="t-text-[#706A85] t-text-[12px] t-leading-[18px]  t-font-[300]">
                            {`${totalTasks} Filings`}
                          </div>
                        )}
                        {!isFetching && entity.action_items_count > 0 && (
                          <div className="t-hidden sm:t-block">
                            <TaskStat
                              title="Actions required"
                              count={entity.action_items_count}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {!isFetching && entity.action_items_count > 0 && (
                    <div className="t-mr-9 sm:t-hidden">
                      <TaskStat
                        title="Actions required"
                        count={entity.action_items_count}
                      />
                    </div>
                  )}
                </div>
              </Accordion.Trigger>
              <Accordion.Content className="t-px-5 t-py-3 sm:t-px-0">
                <EntityTaskTable
                  entityId={entity.uuid}
                  taskSeasonsWithEntity={entity}
                  isParentFetching={isParentFetching}
                  resetFilter={resetFilter}
                  isAnyFilterActive={isAnyFilterActive}
                  params={params}
                />
              </Accordion.Content>
            </Accordion.Item>
          );
        })}
    </Accordion.Root>
  );
};
